<template>
  <div class="filtered-posts">
    <!-- show a brief of all data that there is -->

    <div
      v-if="hasAllBeenLoaded"
      id="dataTableContainer"
      class="my-3"
    >
      <v-data-table
        :headers="tableHeaders"
        :items="tableItems"
        hide-default-footer
        disable-pagination
        class="box-shadow-soft"
      ></v-data-table>
    </div>

    <div class="d-flex flex-wrap justify-space-between align-center">
      <div class="mb-3 mb-md-0">
        <!-- The Section Heading -->
        <div class="text-h4 font-weight-bold mb-4 mt-6">
          Posts&nbsp;
          <span v-if="hasAllBeenLoaded">
            ({{ tableItems[filterBy].posts }})
          </span>
        </div>

        <!-- Filter Select Inputs -->
        <template v-if="hasAllBeenLoaded">
          <!-- Stats for all the data there is -->
          <div class="black--text d-flex flex-column flex-md-row">
            <span class="mr-md-3">
              <b>{{ tableItems[filterBy].category }}: </b> {{ tableItems[filterBy].accounts }}
            </span>
            <span class="mr-md-3">
              <b>Likes: </b> {{ tableItems[filterBy].likes }}
            </span>
            <span class="mr-md-3">
              <b>Views: </b> {{ tableItems[filterBy].views }}
            </span>
            <span>
              <b>Total Followers: </b> {{ tableItems[filterBy].followers }}
            </span>
          </div>

          <!-- Sort options -->
          <div class="d-flex align-center">
            <div class="d-flex align-center mt-3 text-subtitle-2 font-weight-bold">
              <div class="contain-select-width">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  label="Sort By"
                  color="primary"
                  hide-details
                  outlined
                  dense
                ></v-select>
              </div>
            </div>

            <div class="d-flex align-center mt-3 text-subtitle-2 font-weight-bold ml-3">
              <div class="contain-select-width contain-select-width--2x">
                <v-select
                  v-model="filterBy"
                  :items="filterOptions"
                  label="Followers' Range"
                  color="primary"
                  hide-details
                  outlined
                  dense
                ></v-select>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Show the download buttons -->
      <div
        v-if="hasAllBeenLoaded"
        class="d-flex flex-wrap align-center"
      >

        <div class="mr-3">
          <v-btn
            :disabled="shouldPDFButtonBeDisabled"
            :loading="shouldPDFButtonBeDisabled"
            :class="{ 'shadow--primary': !shouldPDFButtonBeDisabled }"
            @click="generatePDF"
            color="primary"
            max-width="100%"
            depressed
          >
            Download PDF
            <v-icon right>download</v-icon>
          </v-btn>
          <div
            v-if="shouldPDFButtonBeDisabled"
            class="mx-auto progress-loader"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            />
          </div>
        </div>

        <v-btn
          :disabled="shouldCSVButtonBeDisabled"
          :loading="shouldCSVButtonBeDisabled"
          :class="{ 'shadow--primary': !shouldCSVButtonBeDisabled }"
          @click="downloadCSV"
          color="primary"
          depressed
        >
          Download CSV
          <v-icon right>download</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- create the grid -->
    <v-row class="mt-6">
      <!-- loop through each post data -->
      <v-col
        cols="12"
        sm="6"
        md="4"
        v-for="item in visiblePosts"
        :key="item.post_id"
      >
        <!-- present it in a card -->
        <v-card class="filtered-post-card box-shadow-sm" outlined>
          <div>
            <!-- show the image attached with the post -->
            <v-img
              height="250"
              :src="item.thumbnail"
              class="cursor-pointer"
              @click="redirectToWeb(item.link)"
            />

            <!-- present the user's and post details -->
            <v-card-text>
              <!-- the header section for user details -->
              <div class="d-flex">
                <!-- user's profile picture -->
                <!-- <v-avatar>
                  <img :src="item.user.picture" />
                </v-avatar> -->
                <v-img
                  contain
                  width="48"
                  height="48"
                  max-width="48"
                  class="border-radius-100"
                  :src="item.user.picture"
                />
                <!-- user's name etc. -->
                <div class="pl-3 d-flex flex-column black--text">
                  <span class="font-weight-bold">
                    @{{ item.user.username }}
                  </span>
                  <span>Followers: {{ nFormatter(item.user.followers) }}</span>
                  <span v-if="item.user.geo">
                    {{ flagEmoji(item.user.geo.country.code) }} {{ item.user.geo.country.name }}
                  </span>
                </div>
              </div>

              <!-- the post date -->
              <div class="caption mt-3">
                {{ dayjs(item.created).format("LLL") }}
              </div>

              <div class="mt-3 black--text">
                {{ (item.text || '').slice(0, 98) + '...' }}
              </div>

              <!-- show likes and comments stats -->
              <v-row class="mt-3">
                <!-- likes -->
                <v-col>
                  <div>
                    Likes
                  </div>
                  <div class="black--text font-weight-bold">
                    {{ nFormatter(item.stat.likes) }}
                  </div>
                </v-col>
                <!-- Views -->
                <v-col v-if="item.type === 'video'">
                  <div>
                    Views
                  </div>
                  <div class="black--text font-weight-bold">
                    {{ nFormatter(item.stat.views) }}
                  </div>
                </v-col>
                <!-- comments -->
                <v-col>
                  <div>
                    Comments
                  </div>
                  <div class="black--text font-weight-bold">
                    {{ nFormatter(item.stat.comments) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </div>

          <!-- the select checkbox button -->
          <!-- <v-card-actions>
            <v-checkbox
              v-model="selectedPosts[item.post_id]"
              @change="handleCheckboxChange"
              label="Select Post"
            ></v-checkbox>
          </v-card-actions> -->
        </v-card>
      </v-col>
    </v-row>

    <div
      id="intersect-detector"
      ref="intersectDetector"
      v-intersect="handleIntersect"
    ></div>
  </div>
</template>

<script>
// Import node packages
import uniqBy from "lodash/uniqBy"

// Import helper objects and functions
import filterOptions from "@/helpers/paid-collaborations/filterOptions.js"

// Export the SFC
export default {
  // Name of the component
  name: "FilteredPosts",

  // Accept incoming data from parent
  props: {
    moduleId: {
      type: [Symbol, String, Number],
      required: true
    }
  },

  // Define local data variables
  data: () => ({
    // selectedPosts: {},
    shouldCSVButtonBeDisabled: false,
    shouldPDFButtonBeDisabled: false,
    triggerId: null,

    sortBy: "date",
    sortOptions: [
      {
        text: "Engagements",
        value: "engagements"
      },
      {
        text: "Recent",
        value: "date"
      }
    ],

    filterBy: 0,
    filterOptions: filterOptions(),

    tableHeaders: [
      {
        text: "Category",
        value: "category",
        sortable: false
      },
      {
        text: "Accounts",
        value: "accounts",
        sortable: false
      },
      {
        text: "Followers",
        value: "followers",
        sortable: false
      },
      {
        text: "Likes",
        value: "likes",
        sortable: false
      },
      {
        text: "Views",
        value: "views",
        sortable: false
      },
      {
        text: "Posts",
        value: "posts",
        sortable: false
      },
    ],

    tableItems: [],

    visiblePostsCount: 3
  }),

  // Define readonly data variables
  computed: {
    /**
     * Get all the posts for this search
     *
     * @returns {Array}
     */
    posts() {
      return this.$store.getters["paidCollaborations/posts"](this.moduleId)?.data || []
    },

    /**
     * Get all the filtered out posts based on the select options
     *
     * @returns {Array}
     */
    filteredPosts() {
      const selectedFilter = this.filterOptions[this.filterBy]

      return this.posts.filter((post) => {
        return post.user.followers >= selectedFilter.min && post.user.followers < selectedFilter.max
      })
    },

    /**
     * Get the filtered posts sorted by the select option
     *
     * @returns {Array}
     */
    sortedPosts() {
      // Foreach select option value
      switch (this.sortBy) {
        case "engagements":
          return this.filteredPosts.sort((a, b) => b.user.engagements - a.user.engagements)
        case "date":
          return this.filteredPosts.sort((a, b) => (new Date(b.created)).getTime() - (new Date(a.created)).getTime())
        default:
          return this.filteredPosts
      }
    },

    /**
     * Get the first few visible posts from the list
     *
     * @returns {Array}
     */
    visiblePosts() {
      return this.sortedPosts.slice(0, this.visiblePostsCount)
    },

    /**
     * If the requests to fetch all the posts have been completed
     *
     * @returns {Boolean}
     */
    hasAllBeenLoaded() {
      return this.$store.getters["paidCollaborations/hasAllBeenLoaded"](this.moduleId)
    }
  },

  // Watch data variables for changes
  watch: {
    // whenever posts array has been updated, find and check all of the items
    posts: {
      handler () {
        this.calculateValues()
        // this.bulkMarkSelected()
      },

      deep: true,
      immediate: true
    },

    // Every time the filter by is changed, reset the visible counter
    filterBy() {
      this.visiblePostsCount = 3
    }
  },

  // Define all the method functions
  methods: {
    // calculateButtonStatus() {
    //   this.shouldCSVButtonBeDisabled = Object.values(this.selectedPosts).filter((val) => val).length === 0
    // },

    /**
     * Calculate stats for all posts
     *
     * @returns {void}
     */
    calculateValues() {
      // const selectedItems = this.posts.filter((item) => this.selectedPosts[item.post_id] === true)
      for (const selectedFilter of this.filterOptions) {
        const selectedItems = this.posts.filter((post) => {
          return post.user.followers >= selectedFilter.min && post.user.followers < selectedFilter.max
        })

        const nonDuplicateAccounts = uniqBy(selectedItems, "user.user_id")

        const followers = nonDuplicateAccounts.reduce((a, b) => a + b.user.followers, 0)
        const likes = selectedItems.reduce((a, b) => a + b.stat.likes, 0)
        const views = selectedItems.reduce((a, b) => a + b.stat.views, 0)
        const accounts = nonDuplicateAccounts.length

        this.tableItems.push({
          category: selectedFilter.title,
          followers: nFormatter(followers),
          accounts: nFormatter(accounts),
          likes: nFormatter(likes),
          views: nFormatter(views),
          posts: nFormatter(selectedItems.length)
        })
      }
    },

    /**
     * Called when the post's checkbox has been changed
     */
    // handleCheckboxChange() {
    //   this.calculateValues()
    //   this.calculateButtonStatus()
    // },

    /*
    bulkMarkSelected() {
      for (const item of this.posts) {
        // if it's not unselected by the user, select it
        if (this.selectedPosts[item.post_id] !== false) {
          this.selectedPosts[item.post_id] = true
        }
      }

      // once done, calculate the values again
      this.calculateValues()

      // and should the button be active
      this.calculateButtonStatus()
    },
    */

    /**
     * Convert the items into a JSON object and convert it into CSV
     *
     * @returns {Promise<void>}
     */
    async downloadCSV() {
      this.shouldCSVButtonBeDisabled = true
      try {
        await this.$store.dispatch("paidCollaborations/downloadPostsAsCSV", this.moduleId)
      } catch (error) {
        logger({ type: "JSON to CSV error", error })

        this.$store.dispatch("toasts/add", { text: "An error occurred while downloading CSV." })
      } finally {
        this.shouldCSVButtonBeDisabled = false
      }
    },

    /**
     * Make a network request to generate the PDF for this report
     *
     * @returns {Promise<void>}
     */
    async generatePDF() {
      // If already downloading, don't continue
      if (this.shouldPDFButtonBeDisabled) return

      // Otherwise
      this.triggerId = String(Date.now())
      this.shouldPDFButtonBeDisabled = true

      try {
        await this.$store.dispatch("paidCollaborations/downloadPostsAsPDF", {
          moduleId: this.moduleId,
          triggerId: this.triggerId
        })
      } catch (error) {
        logger({ type: "An error occurred, please try again.", error })

        this.$store.dispatch("toasts/add", { text: "An error occurred while downloading CSV." })
      }
    },

    /**
     * Stop the download progress
     *
     * @param {Event} event
     * @returns {void}
     */
    stopDownloadProgressBar(event) {
      // If the triggerId matches
      if (event.detail.data.misc.triggerId === this.triggerId) {
        this.shouldPDFButtonBeDisabled = false
      }
    },

    /**
     * Whether or not the user has scrolled past the intersection limit
     *
     * @param {Object} entries
     * @param {Object} observer
     * @param {Boolean} isIntersecting
     */
    handleIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.visiblePostsCount += 3
      }
    }
  },

  /**
   * As soon as the component's data is ready
   *
   * @returns {void}
   */
  created() {
    // Keep listening for a download event
    window.addEventListener("ReadyToDownload", this.stopDownloadProgressBar)
  },

  /**
   * Before this component is about to be removed
   *
   * @returns {void}
   */
  beforeDestroy() {
    // De-register the event listener
    window.removeEventListener("ReadyToDownload", this.stopDownloadProgressBar)
  }
}
</script>

<style lang="stylus">
// stretch the card and push the checkbox to bottommost part
.filtered-posts
  .filtered-post-card
    height 100%
    display flex
    border-radius 8px
    overflow hidden
    flex-direction column
    justify-content space-between
    transition all .25s cubic-bezier(.645, .045, .355, 1)

    &:hover
      transform translateY(-4px)
      box-shadow 0 15px 37px 0 rgba(0, 147, 178, .10), 0 15px 37px 0 rgba(0, 0, 0, .15)

  .contain-select-width
    width 12em

    &--2x
      width 15em

    .v-select
      font-size 14px

  .progress-loader
    width 100%
    border-radius 0 0 16px 16px
    overflow hidden
    margin-top -1px
</style>
